/**
 * Which table did the player just try to sit at?
 *
 * The engine answers a seat request on `gotJoinGame`, but its most common
 * rejection — the Redis action-lock "Seat taken! Please choose another empty
 * spot." (apps/engine/texas/socket.js userJoinGame) — is emitted BEFORE the
 * handler fills in `tableId`, so that frame carries nothing but
 * `response.error`. The other rejections ("Invalid Seat #N!", "Table is already
 * full!", "You are already seated!") do include `tableId`.
 *
 * With several tables open the client therefore cannot always tell, from the
 * frame alone, which felt the error belongs to — which is why it used to be
 * thrown up as one app-wide modal over the whole grid. `sitDown` is the single
 * emit point for a seat request and already knows the explicit target table
 * (services/joinGameParams Rule 1), so it records the attempt here and the
 * `gotJoinGame` handler falls back to it when the frame is silent.
 *
 * One record PER TABLE, not one for the whole app. Several joins really can be
 * in flight at once: every grid cell mounts its own GlobalProvider, so
 * `openAskBuyin` is already per-table there and pages/Game.tsx mounts a
 * BuyInModal per cell (hooks/useTableScopedOpen, lines 26-36), and the deferred
 * seat-claim send (BuyInModal.tsx:275) fires outside the click. A single slot
 * let the second attempt overwrite the first, so a silent rejection belonging to
 * table A was shown on table B.
 *
 * The rule for a frame that names no table is therefore "answer only when the
 * answer is unambiguous": exactly one live attempt. With two in flight we return
 * null and the caller falls back to the app-level modal — the old, imprecise
 * behaviour — rather than confidently blaming the wrong felt. Two things keep
 * that rule honest: the TTL retires an attempt whose response never arrived, and
 * an ANSWERED attempt is retired at once — on success App.tsx calls
 * `clearPendingSeatJoin`, on a rejection `resolveAndRetireSeatJoinError` does it
 * while naming the table — so a finished join can never be the last one
 * standing for someone else's error.
 */

export type PendingSeatJoin = {
  gameID: string;
  /** Requested seat, or null when the engine was left to pick one. */
  seatId: number | null;
  at: number;
};

/** Well past any healthy join round trip; short enough that a lost response
 *  cannot pin a later, unrelated rejection to the wrong table. */
export const PENDING_SEAT_JOIN_TTL_MS = 15000;

/** Keyed by normalised gameID — one live attempt per table. */
const pending = new Map<string, PendingSeatJoin>();

const dropExpired = (nowMs: number): void => {
  pending.forEach((attempt, gameID) => {
    if (nowMs - attempt.at > PENDING_SEAT_JOIN_TTL_MS) pending.delete(gameID);
  });
};

const normaliseGameID = (gameID: unknown): string => {
  const s = gameID === null || gameID === undefined ? '' : String(gameID).trim();
  return s === '0' ? '' : s;
};

const normaliseSeat = (seatId: unknown): number | null => {
  const n = Number(seatId);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export const rememberSeatJoin = (gameID: unknown, seatId: unknown, nowMs = Date.now()): void => {
  const gid = normaliseGameID(gameID);
  dropExpired(nowMs);
  if (!gid) {
    // A join went out that we cannot name (an unhydrated slice emits "0"). No
    // later silent rejection can be safely attributed to anyone while it is
    // outstanding, so forget the lot rather than answer with a table that may
    // not be the one that just failed.
    pending.clear();
    return;
  }
  pending.set(gid, { gameID: gid, seatId: normaliseSeat(seatId), at: nowMs });
};

/**
 * The one live attempt, or null when the answer would be a guess — nothing
 * pending, or more than one table waiting on a response.
 */
export const peekPendingSeatJoin = (nowMs = Date.now()): PendingSeatJoin | null => {
  dropExpired(nowMs);
  if (pending.size !== 1) return null;
  return pending.values().next().value ?? null;
};

/**
 * Forget an attempt the engine has answered. Called with the table from a
 * successful `gotJoinGame` (App.tsx); with no resolvable table — a success frame
 * that named none — forget every attempt, since we cannot tell which one just
 * completed and a completed attempt must never answer for a later failure.
 */
export const clearPendingSeatJoin = (gameID?: unknown): void => {
  const gid = normaliseGameID(gameID);
  if (!gid) {
    pending.clear();
    return;
  }
  pending.delete(gid);
};

/** Test-only reset of module state. */
export const __resetPendingSeatJoin = (): void => {
  pending.clear();
};

/**
 * The table a `gotJoinGame` rejection belongs to: the frame's own `tableId`
 * when the engine supplied one, else the attempt we remembered. Null when
 * neither is known — the caller should then fall back to the app-level error
 * modal rather than guess.
 */
export const resolveSeatJoinErrorTable = (
  frame: any,
  pendingJoin: PendingSeatJoin | null
): { gameID: string; seatId: number | null } | null => {
  const fromFrame = normaliseGameID(frame?.tableId ?? frame?.gameID);
  if (fromFrame) {
    return { gameID: fromFrame, seatId: normaliseSeat(frame?.seatId) ?? pendingJoin?.seatId ?? null };
  }
  if (pendingJoin) return { gameID: pendingJoin.gameID, seatId: pendingJoin.seatId };
  return null;
};

/**
 * What the `gotJoinGame` error branch asks for: the table to show the rejection
 * on, with that attempt retired in the same breath.
 *
 * A rejection is as terminal as a success — the engine has answered that join.
 * Left pending, the dead attempt stayed live for the rest of the TTL, so the
 * NEXT table's bare "Seat taken!" saw two entries, could not be named, and fell
 * back to the app-wide modal this ticket exists to remove. Only the answered
 * table is retired; a genuinely ambiguous frame (null) retires nothing, since
 * the attempt it belongs to is still waiting on its own response.
 */
export const resolveAndRetireSeatJoinError = (
  frame: any,
  nowMs = Date.now()
): { gameID: string; seatId: number | null } | null => {
  const target = resolveSeatJoinErrorTable(frame, peekPendingSeatJoin(nowMs));
  if (target) clearPendingSeatJoin(target.gameID);
  return target;
};
